import React, { useEffect, useState } from 'react';
import MenuBar from '../../../layout/MenuBar';
import {
  Card, Typography, Alert, Tooltip,
  Button, Input, Space, Tag, message,
} from 'antd';
import AppModal from '../../../layout/popup';
import {
  KeyOutlined, CopyOutlined, ReloadOutlined, DeleteOutlined, CheckCircleOutlined,
  LockOutlined, WarningOutlined, HomeOutlined, EnvironmentOutlined,
  MailOutlined, CrownOutlined,
} from '@ant-design/icons';
import {
  getProfile_API,
  generateApiToken_API,
  revokeApiToken_API,
} from '../../../../functions/membertData';
import { useSelector } from 'react-redux';
import AuthVerifyScreen from '../../../../routes/AuthVerifyScreen';
import styles from './Profile.module.css';

const { Text, Paragraph } = Typography;

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [confirmDate, setConfirmDate] = useState('');
  const user = useSelector((state) => state.user);

  const authToken = user?.token || localStorage.getItem('token');

  const fetchProfile = async () => {
    if (!authToken) {
      setError('No auth token');
      setLoading(false);
      return;
    }
    try {
      const res = await getProfile_API(authToken);
      if (res.success) setProfile(res.data);
      else setError(res.message || 'Failed to load profile');
    } catch (err) {
      console.error('Profile fetch error', err);
      setError(err.message || 'Error fetching profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // เปิด modal ยืนยันรหัสผ่านก่อนสร้าง token
  const openGenerateModal = () => {
    setConfirmPassword('');
    setShowGenerateModal(true);
  };

  // สร้าง API token ใหม่ (หลังยืนยันรหัสผ่าน)
  const handleGenerateToken = async () => {
    if (!confirmPassword.trim()) {
      message.warning('กรุณากรอกรหัสผ่าน');
      return;
    }
    setGenerateLoading(true);
    try {
      const res = await generateApiToken_API(authToken, confirmPassword.trim());
      if (res.success) {
        message.success('สร้าง API token สำเร็จ');
        setProfile((prev) => ({ ...prev, apiToken: res.apiToken }));
        setShowGenerateModal(false);
        setConfirmPassword('');
      } else {
        message.error(res.message || 'เกิดข้อผิดพลาด');
      }
    } catch (err) {
      message.error(err.message || 'ไม่สามารถสร้าง token ได้');
    } finally {
      setGenerateLoading(false);
    }
  };

  // วันที่ปัจจุบัน DDMMYYYY (พ.ศ.)
  const getTodayBEString = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyyBE = d.getFullYear() + 543;
    return `${dd}${mm}${yyyyBE}`;
  };

  // เปิด modal ยืนยันการยกเลิก token
  const openRevokeModal = () => {
    setConfirmDate('');
    setShowRevokeModal(true);
  };

  // ยกเลิก token (หลังยืนยันวันที่)
  const handleRevokeToken = async () => {
    const today = getTodayBEString();
    if (confirmDate.trim() !== today) {
      message.error('กรุณาพิมพ์วันที่ปัจจุบันให้ถูกต้อง');
      return;
    }
    setRevokeLoading(true);
    try {
      const res = await revokeApiToken_API(authToken);
      if (res.success) {
        message.success('ยกเลิก API token สำเร็จ');
        setProfile((prev) => ({ ...prev, apiToken: null }));
        setShowRevokeModal(false);
        setConfirmDate('');
      } else {
        message.error(res.message || 'เกิดข้อผิดพลาด');
      }
    } catch (err) {
      message.error('ไม่สามารถยกเลิก token ได้');
    } finally {
      setRevokeLoading(false);
    }
  };

  // คัดลอก token
  const handleCopy = () => {
    if (!profile?.apiToken) return;
    navigator.clipboard.writeText(profile.apiToken).then(() => {
      setCopied(true);
      message.success('คัดลอก token แล้ว');
      setTimeout(() => setCopied(false), 2000);
    });
  };



  if (loading) return (
    <AuthVerifyScreen
      message="กำลังโหลดข้อมูลโปรไฟล์"
      subMessage="กรุณารอสักครู่..."
    />
  );
  if (error) return (
    <div className={styles.pageWrapper}>
      <div className={styles.menuBarFixed}><MenuBar /></div>
      <div className={styles.contentArea}><Alert type="error" message={error} /></div>
    </div>
  );
  if (!profile) return null;

  return (
    <div className={`${styles.pageWrapper} page-background page-dashboard`}>
      <div className={styles.menuBarFixed}>
        <MenuBar />
      </div>

      <div className={styles.contentArea}>
        {/* ===== ข้อมูลโปรไฟล์ ===== */}
        <Card className={`${styles.profileCard} ${styles.profileInfoCard}`} bordered={false}>
          {/* Header */}
          <div className={styles.profileHeader}>
            <div className={styles.profileAvatar}>
              <HomeOutlined />
            </div>
            <div className={styles.profileHeaderText}>
              <h2 className={styles.profileName}>{profile.village}</h2>
              <div className={styles.profileBadges}>
                <Tag className={styles.phaseBadge}>Phase {profile.phase}</Tag>
                <Tag
                  icon={<CrownOutlined />}
                  className={styles.roleBadge}
                >
                  {profile.role}
                </Tag>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className={styles.profileDivider} />

          {/* Info Grid */}
          <div className={styles.profileGrid}>
            <div className={styles.profileItem}>
              <div className={styles.profileItemIcon}><EnvironmentOutlined /></div>
              <div className={styles.profileItemContent}>
                <span className={styles.profileItemLabel}>จังหวัด</span>
                <span className={styles.profileItemValue}>{profile.province}</span>
              </div>
            </div>
            <div className={styles.profileItem}>
              <div className={styles.profileItemIcon}><EnvironmentOutlined /></div>
              <div className={styles.profileItemContent}>
                <span className={styles.profileItemLabel}>อำเภอ / เขต</span>
                <span className={styles.profileItemValue}>{profile.district}</span>
              </div>
            </div>
            <div className={styles.profileItem}>
              <div className={styles.profileItemIcon}><EnvironmentOutlined /></div>
              <div className={styles.profileItemContent}>
                <span className={styles.profileItemLabel}>ตำบล / แขวง</span>
                <span className={styles.profileItemValue}>{profile.subdistrict}</span>
              </div>
            </div>
            <div className={styles.profileItem}>
              <div className={styles.profileItemIcon}><MailOutlined /></div>
              <div className={styles.profileItemContent}>
                <span className={styles.profileItemLabel}>อีเมล</span>
                <span className={styles.profileItemValue}>{profile.email || '-'}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* ===== API Token Section ===== */}
        <Card
          className={`${styles.profileCard} ${styles.tokenCard}`}
          style={{ marginTop: 16 }}
          title={
            <div className={styles.tokenCardHeader}>
              <div className={styles.tokenCardTitle}>
                <div className={styles.tokenIconBadge}>
                  <KeyOutlined />
                </div>
                <span>API Token</span>
                {profile.apiToken
                  ? <Tag color="green" className={styles.tokenStatusTag}>Active</Tag>
                  : <Tag color="default" className={styles.tokenStatusTag}>ยังไม่มี token</Tag>}
              </div>
              <div className={styles.tokenCardActions}>
                {profile.apiToken ? (
                  <Space size={8}>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={openGenerateModal}
                      loading={generateLoading}
                      className={styles.tokenActionBtn}
                    >
                      สร้างใหม่
                    </Button>
                    <Button icon={<DeleteOutlined />} danger loading={revokeLoading} className={styles.tokenActionBtn} onClick={openRevokeModal}>
                      ยกเลิก
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    icon={<KeyOutlined />}
                    onClick={openGenerateModal}
                    loading={generateLoading}
                    className={styles.tokenGenerateBtn}
                  >
                    สร้าง API Token
                  </Button>
                )}
              </div>
            </div>
          }
          bordered={false}
        >
          <Paragraph type="secondary" className={styles.tokenDescription}>
            ใช้ token นี้เพื่อเรียก API ดึงข้อมูลสมาชิกและยานพาหนะจากภายนอกระบบ<br />
            ส่ง header: <Text code>Authorization: Bearer &lt;token&gt;</Text>
          </Paragraph>

          {/* แสดง token */}
          {profile.apiToken && (
            <div className={styles.tokenDisplayRow}>
              <Tooltip title={profile.apiToken} placement="top">
                <Input
                  value={profile.apiToken}
                  readOnly
                  className={styles.tokenInput}
                  suffix={
                    <Tooltip title="คัดลอก token">
                      <Button
                        size="small"
                        type="text"
                        className={styles.tokenCopyBtn}
                        icon={copied ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
                        onClick={handleCopy}
                      />
                    </Tooltip>
                  }
                />
              </Tooltip>
            </div>
          )}

          {/* Endpoints */}
          {profile.apiToken && (
            <div className={styles.tokenEndpoints}>
              <Text type="secondary" className={styles.endpointLabel}>Endpoints</Text>
              <div className={styles.endpointList}>
                <Tag className={styles.endpointTag}>
                  <span className={styles.endpointMethodPost}>POST</span>
                  /api/auth/login
                </Tag>
                <Tag className={styles.endpointTag}>
                  <span className={styles.endpointMethod}>GET</span>
                  /api/members
                </Tag>
                <Tag className={styles.endpointTag}>
                  <span className={styles.endpointMethod}>GET</span>
                  /api/members/:id
                </Tag>
                <Tag className={styles.endpointTag}>
                  <span className={styles.endpointMethod}>GET</span>
                  /api/vehicles
                </Tag>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* ===== Modal ยืนยันรหัสผ่านก่อนสร้าง/เปลี่ยน Token ===== */}
      <AppModal
        icon={<LockOutlined />}
        title="ยืนยันตัวตนเพื่อสร้าง API Token"
        subtitle="กรุณากรอกรหัสผ่านเพื่อยืนยันการดำเนินการ"
        open={showGenerateModal}
        onCancel={() => { setShowGenerateModal(false); setConfirmPassword(''); }}
        onOk={handleGenerateToken}
        okText="ยืนยันและสร้าง Token"
        cancelText="ยกเลิก"
        confirmLoading={generateLoading}
        okButtonProps={{ disabled: !confirmPassword.trim() }}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            type="warning"
            icon={<WarningOutlined />}
            showIcon
            message="คำเตือน"
            description="เมื่อ Token ถูกเปลี่ยนแปลง อุปกรณ์ที่เคยเชื่อมต่อกับระบบด้วย Token เดิมจะหลุดการเชื่อมต่อทั้งหมด และจำเป็นต้องใช้ Token ใหม่ในการเชื่อมต่ออีกครั้ง"
            style={{ borderRadius: 10 }}
          />
        </div>
        <Input.Password
          prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
          placeholder="กรอกรหัสผ่าน"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onPressEnter={handleGenerateToken}
          size="large"
          style={{ borderRadius: 10 }}
          autoFocus
        />
      </AppModal>

      {/* ===== Modal ยืนยันการยกเลิก Token ด้วยวันที่ ===== */}
      <AppModal
        icon={<DeleteOutlined />}
        title="ยืนยันการยกเลิก API Token"
        subtitle="การดำเนินการนี้ไม่สามารถย้อนกลับได้"
        open={showRevokeModal}
        onCancel={() => { setShowRevokeModal(false); setConfirmDate(''); }}
        onOk={handleRevokeToken}
        okText="ยืนยันยกเลิก Token"
        cancelText="ปิด"
        confirmLoading={revokeLoading}
        okButtonProps={{
          danger: true,
          disabled: confirmDate.trim() !== getTodayBEString(),
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            type="error"
            icon={<WarningOutlined />}
            showIcon
            message="คำเตือน: ยกเลิก Token อย่างถาวร"
            description="เมื่อยกเลิก Token แล้ว อุปกรณ์และระบบภายนอกทั้งหมดที่เชื่อมต่อด้วย Token นี้จะหยุดทำงานทันที ไม่สามารถกู้คืน Token เดิมได้"
            style={{ borderRadius: 10 }}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            พิมพ์วันที่ปัจจุบัน (วัน/เดือน/ปี พ.ศ. เช่น 01012569) เพื่อยืนยันการยกเลิก
          </Text>
        </div>
        <Input
          prefix={<WarningOutlined style={{ color: '#ff4d4f' }} />}
          placeholder="เช่น 01012569"
          value={confirmDate}
          onChange={(e) => setConfirmDate(e.target.value)}
          onPressEnter={() => {
            if (confirmDate.trim() === getTodayBEString()) handleRevokeToken();
          }}
          size="large"
          style={{ borderRadius: 10 }}
          autoFocus
          status={confirmDate && confirmDate.trim() !== getTodayBEString() ? 'error' : undefined}
        />
        {confirmDate && confirmDate.trim() !== getTodayBEString() && (
          <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            วันที่ไม่ตรงกับวันปัจจุบัน (รูปแบบ เช่น 01012569)
          </Text>
        )}
      </AppModal>
    </div>
  );
};

export default Profile;
